export const SEEKA_TRACKING_TYPE = 'Seeka';

type SeekaDataPreferencesRaw = {
  firstName?: boolean;
  lastName?: boolean;
  email?: boolean;
  phone?: boolean;
};

type TrackingBeaconRaw = {
  resource_uri: string;
  id: string;
  tracking_type: string;
  event_id: string | null;
  user_id: string | null;
  organizer_id: string | null;
  pixel_id: string;
  public_key?: string;
  org_id?: string;
  triggers: Record<string, string>;
  // Per-field flags controlling which authenticated-user PII may be sent to
  // Seeka (set by the organizer in the conversions manager). Any field omitted
  // defaults to disabled — no PII sent.
  data_preferences?: SeekaDataPreferencesRaw;
};

export type TrackingBeaconsPagination = {
  object_count?: number;
  page_number?: number;
  page_size?: number;
  page_count?: number;
  has_more_items?: boolean;
};

export type TrackingBeaconsResponse = {
  tracking_beacons?: TrackingBeaconRaw[];
  pagination?: TrackingBeaconsPagination;
};

export type TrackingBeacon = {
  pixelId: string;
  id: string;
  events: Record<string, string>;
};

// Normalized, fully-populated per-field consent flags. Missing/unknown fields
// collapse to `false` so the privacy-safe default is "send nothing".
export type SeekaDataPreferences = {
  firstName: boolean;
  lastName: boolean;
  email: boolean;
  phone: boolean;
};

// Seeka beacons carry three credentials (org / public key / instance) instead
// of a single pixelId. The BE reuses `pixel_id` as the per-instance scope and
// adds `org_id` + `public_key` for the org-level credentials.
export type SeekaTrackingBeacon = {
  orgId: string;
  publicKey: string;
  instanceId: string;
  id: string;
  events: Record<string, string>;
  dataPreferences: SeekaDataPreferences;
};

export type TrackingBeacons = Record<string, Array<TrackingBeacon | SeekaTrackingBeacon>>;

// Only an explicit `true` enables a field; anything else (false / missing /
// non-boolean) counts as disabled.
const normalizeSeekaDataPreferences = (prefs?: SeekaDataPreferencesRaw): SeekaDataPreferences => ({
  firstName: prefs?.firstName === true,
  lastName: prefs?.lastName === true,
  email: prefs?.email === true,
  phone: prefs?.phone === true,
});

const toSeekaBeacon = (beacon: TrackingBeaconRaw): SeekaTrackingBeacon => ({
  orgId: beacon.org_id ?? '',
  publicKey: beacon.public_key ?? '',
  instanceId: beacon.pixel_id,
  id: beacon.id,
  events: beacon.triggers,
  dataPreferences: normalizeSeekaDataPreferences(beacon.data_preferences),
});

const toGenericBeacon = (beacon: TrackingBeaconRaw): TrackingBeacon => ({
  pixelId: beacon.pixel_id,
  id: beacon.id,
  events: beacon.triggers,
});

export const transformTrackingBeacons = (response: TrackingBeaconsResponse | undefined): TrackingBeacons | null => {
  if (!response?.tracking_beacons?.length) {
    return null;
  }

  const trackingBeacons: TrackingBeacons = {};

  response.tracking_beacons.forEach((beacon) => {
    const transformed = beacon.tracking_type === SEEKA_TRACKING_TYPE ? toSeekaBeacon(beacon) : toGenericBeacon(beacon);
    trackingBeacons[beacon.tracking_type] = [...(trackingBeacons[beacon.tracking_type] ?? []), transformed];
  });

  return trackingBeacons;
};

// Merges a follow-up page's beacons into the accumulated response. Backend
// pages are ordered by id (EB-323621), so concatenation preserves a stable,
// de-duplicated ordering across pages.
export const mergeTrackingBeaconsResponses = (
  accumulated: TrackingBeaconsResponse,
  nextPage: TrackingBeaconsResponse,
): TrackingBeaconsResponse => ({
  tracking_beacons: [...(accumulated.tracking_beacons ?? []), ...(nextPage.tracking_beacons ?? [])],
  pagination: nextPage.pagination,
});
